/**
 * What the business spent, for any run of days.
 *
 * The Expenses tab is where costs are entered, a day at a time. This is where
 * the owner reads them back: every entry between two dates, or the same money
 * added up by category or by day, one category or all, on screen, on paper,
 * or taken away as a spreadsheet or a PDF. Dated by the trading day each
 * expense was booked to, so it agrees with the tab and the audit report.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR, today, dateKey } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ReportExport } from './ReportExport.jsx';
import { ReportTabs } from './ReportTabs.jsx';
import { prettyDay } from './period.jsx';

const startOfMonth = (day) => `${day.slice(0, 7)}-01`;
const box = 'h-10 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink';

export default function ExpenseReportPage() {
  const [from, setFrom] = useState(() => startOfMonth(today()));
  const [to, setTo] = useState(today);
  const [by, setBy] = useState('expense');
  const [categoryId, setCategoryId] = useState('');

  const query = { start_date: from, end_date: to, group_by: by, ...(categoryId ? { category_id: categoryId } : {}) };
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', 'expenses', from, to, by, categoryId],
    queryFn: () => api.get(withQuery('/reports/expenses', query)),
  });
  const categories = useQuery({ queryKey: ['expense-categories'], queryFn: () => api.get('/accounts/expense-categories') });

  function setMonthsAgo(months) {
    const start = new Date();
    start.setMonth(start.getMonth() - months, 1);
    const end = months === 0 ? new Date() : new Date(start.getFullYear(), start.getMonth() + 1, 0);
    setFrom(dateKey(start));
    setTo(dateKey(end));
  }

  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const columns = data?.columns ?? [];
  const categoryName = (categories.data?.categories ?? []).find((c) => c.id === categoryId)?.name;
  const period = from === to ? prettyDay(from) : `${prettyDay(from)} – ${prettyDay(to)}`;
  const cell = (col, row) => {
    const v = row[col.key];
    if (col.type === 'money') return formatPKR(v ?? 0);
    if (col.key === 'day' && /^\d{4}-\d{2}-\d{2}$/.test(String(v))) return prettyDay(v);
    return v ?? '';
  };

  return (
    <div className="mx-auto max-w-6xl p-4">
      <ReportTabs />
      <div className="no-print mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Expenses</h1>
          <p className="text-sm text-ink-3">What the business spent, by entry, by category or by day</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ReportExport path={withQuery('/reports/expenses', query)} name={`Expense report ${from} to ${to}`} />
          <Button onClick={() => window.print()} disabled={!data}>
            Print
          </Button>
        </div>
      </div>

      <div className="no-print mb-5 flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">From</span>
          <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} aria-label="From day" className={box} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">To</span>
          <input type="date" value={to} min={from} max={today()} onChange={(e) => e.target.value && setTo(e.target.value)} aria-label="To day" className={box} />
        </label>
        <button type="button" onClick={() => { setFrom(today()); setTo(today()); }} className="h-10 rounded-lg border border-rule px-3 text-sm text-ink-2 hover:text-ink">
          Today
        </button>
        <button type="button" onClick={() => setMonthsAgo(0)} className="h-10 rounded-lg border border-rule px-3 text-sm text-ink-2 hover:text-ink">
          This month
        </button>
        <button type="button" onClick={() => setMonthsAgo(1)} className="h-10 rounded-lg border border-rule px-3 text-sm text-ink-2 hover:text-ink">
          Last month
        </button>
        <label className="ml-auto text-sm">
          <span className="mb-1 block text-xs text-ink-3">Category</span>
          <select aria-label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={box}>
            <option value="">All categories</option>
            {(categories.data?.categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">Show</span>
          <select aria-label="How to group" value={by} onChange={(e) => setBy(e.target.value)} className={box}>
            <option value="expense">Every expense</option>
            <option value="category">Totals by category</option>
            <option value="day">Totals by day</option>
          </select>
        </label>
      </div>

      {isLoading && <Spinner label="Adding up the expenses" />}
      {isError && (
        <p role="alert" className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {error?.message ?? 'That report could not be produced.'}
        </p>
      )}

      {data && (
        <div className="statement rounded-xl border border-rule bg-surface p-4">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold text-ink">{data.title}</h2>
              <p className="text-xs text-ink-3">
                {period}
                {categoryName ? ` · ${categoryName}` : ''} · {data.count ?? rows.length} entries
              </p>
            </div>
            <p className="text-lg font-semibold tabular-nums text-ink">{formatPKR(data.total ?? totals?.amount ?? 0)}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-rule text-left text-xs text-ink-3">
                  {columns.map((col) => (
                    <th key={col.key} className={`px-3 py-2 font-medium ${col.type === 'money' || col.type === 'number' ? 'text-right' : ''}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-6 text-center text-ink-3">
                      Nothing was spent in this period.
                    </td>
                  </tr>
                )}
                {rows.map((row, i) => (
                  <tr key={row.number ?? row.category ?? row.day ?? i} className="border-b border-rule/50">
                    {columns.map((col) => (
                      <td key={col.key} className={`px-3 py-2 ${col.type === 'money' || col.type === 'number' ? 'text-right tabular-nums text-ink' : 'text-ink-2'} ${['day', 'number', 'amount', 'method'].includes(col.key) ? 'whitespace-nowrap' : ''}`}>
                        {cell(col, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {totals && rows.length > 0 && (
                <tfoot>
                  <tr className="font-medium">
                    {columns.map((col) => (
                      <td key={col.key} className={`px-3 py-2.5 text-ink ${col.type === 'money' || col.type === 'number' ? 'text-right tabular-nums' : ''}`}>
                        {totals[col.key] === undefined ? '' : col.type === 'money' ? formatPKR(totals[col.key]) : totals[col.key]}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {data.note && <p className="mt-3 text-xs text-ink-3">{data.note}</p>}
        </div>
      )}
    </div>
  );
}
